// src/app/press-release-detail/press-release-detail.component.ts

import {
  Component,
  Inject,
  OnInit,
  Optional,
  PLATFORM_ID,
  RESPONSE_INIT
} from '@angular/core';

import {
  ActivatedRoute,
  Router,
  RouterModule
} from '@angular/router';

import { Meta } from '@angular/platform-browser';

import {
  CommonModule,
  DatePipe,
  isPlatformServer
} from '@angular/common';

import { PressReleaseService } from '../services/press-release.service';

import {
  createPressReleaseSlug,
  PressRelease
} from '../models/press-release';

import { SeoService } from '../services/seo.service';

// Only if you are using ngx-markdown
import { MarkdownModule } from 'ngx-markdown';

import {
  SocialShareComponent
} from '../components/social-share/social-share.component';

@Component({
  selector: 'app-press-release-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DatePipe,
    MarkdownModule,
    SocialShareComponent
  ],
  templateUrl: './press-release-detail.component.html',
  styleUrls: ['./press-release-detail.component.css']
})
export class PressReleaseDetailComponent implements OnInit {
  pressRelease?: PressRelease;

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly newsroomId =
    `${this.baseUrl}/newsroom#collection`;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private pressReleaseService: PressReleaseService,
    private meta: Meta,
    private seo: SeoService,
    @Inject(PLATFORM_ID)
    private platformId: object,
    @Optional()
    @Inject(RESPONSE_INIT)
    private responseInit: ResponseInit | null
  ) {}

  ngOnInit(): void {
    const idOrSlug =
      this.route.snapshot.paramMap.get('idOrSlug');

    if (!idOrSlug) {
      this.router.navigate(['/newsroom']);
      return;
    }

    this.pressReleaseService
      .getPressReleaseByIdentifier(idOrSlug)
      .subscribe(release => {
        if (!release) {
          this.router.navigate(['/newsroom']);
          return;
        }

        const slug =
          createPressReleaseSlug(release);

        /*
         * Numeric newsroom URLs were published previously.
         * Preserve them for visitors and crawlers while
         * consolidating indexing signals on the canonical slug.
         */
        if (
          release.id === idOrSlug &&
          slug !== idOrSlug
        ) {
          if (
            isPlatformServer(this.platformId) &&
            this.responseInit
          ) {
            this.responseInit.status = 301;
          }

          this.router.navigate(
            ['/newsroom', slug],
            { replaceUrl: true }
          );

          return;
        }

        this.pressRelease = release;

        const articleUrl =
          `${this.baseUrl}/newsroom/${slug}`;

        const articleId =
          `${articleUrl}#article`;

        /*
         * PRESS RELEASE IMAGE
         */
        const releaseImage =
          this.getReleaseImage(release);

        /*
         * STANDARD PAGE SEO
         */
        this.seo.update(
          release.title,
          release.summary,
          `newsroom/${slug}`
        );

        /*
         * BASIC META DESCRIPTION
         */
        this.meta.updateTag({
          name: 'description',
          content: release.summary
        });

        /*
         * Keywords are intentionally limited.
         * Page content and structured data are more important
         * than the keywords meta tag.
         */
        this.meta.updateTag({
          name: 'keywords',
          content: [
            'Lydia Christensen',
            'Lydia Christensen for U.S. Senate',
            'Michigan',
            'press release',
            'newsroom',
            'Ironwood Michigan',
            'U.S. Senate'
          ].join(', ')
        });

        /*
         * OPEN GRAPH
         */
        this.meta.updateTag({
          property: 'og:type',
          content: 'article'
        });

        this.meta.updateTag({
          property: 'og:title',
          content: release.title
        });

        this.meta.updateTag({
          property: 'og:description',
          content: release.summary
        });

        this.meta.updateTag({
          property: 'og:url',
          content: articleUrl
        });

        this.meta.updateTag({
          property: 'og:site_name',
          content: 'Lydia Christensen for U.S. Senate'
        });

        this.meta.updateTag({
          property: 'og:locale',
          content: 'en_US'
        });

        this.meta.updateTag({
          property: 'og:image',
          content: releaseImage
        });

        this.meta.updateTag({
          property: 'og:image:alt',
          content: release.title
        });

        /*
         * ARTICLE OPEN GRAPH DATA
         */
        this.meta.updateTag({
          property: 'article:published_time',
          content: release.date.toISOString()
        });

        this.meta.updateTag({
          property: 'article:modified_time',
          content: release.date.toISOString()
        });

        this.meta.updateTag({
          property: 'article:author',
          content: 'Lydia Christensen'
        });

        this.meta.updateTag({
          property: 'article:section',
          content: 'Press Releases'
        });

        /*
         * X / TWITTER CARD
         */
        this.meta.updateTag({
          name: 'twitter:card',
          content: 'summary_large_image'
        });

        this.meta.updateTag({
          name: 'twitter:title',
          content: release.title
        });

        this.meta.updateTag({
          name: 'twitter:description',
          content: release.summary
        });

        this.meta.updateTag({
          name: 'twitter:image',
          content: releaseImage
        });

        this.meta.updateTag({
          name: 'twitter:image:alt',
          content: release.title
        });

        /*
         * NEWSARTICLE STRUCTURED DATA
         *
         * Connects the individual press release to:
         * - Lydia's Person entity
         * - Campaign Organization entity
         * - Newsroom collection
         * - Website entity
         */
        this.seo.setStructuredData({
          '@context': 'https://schema.org',

          '@graph': [
            {
              '@type': 'NewsArticle',

              '@id': articleId,

              'url': articleUrl,

              'mainEntityOfPage': {
                '@type': 'WebPage',
                '@id': articleUrl
              },

              'isPartOf': {
                '@id': this.newsroomId
              },

              'headline':
                release.title,

              'description':
                release.summary,

              'image': [
                releaseImage
              ],

              'datePublished':
                release.date.toISOString(),

              'dateModified':
                release.date.toISOString(),

              'author': {
                '@id': this.seo.lydiaId
              },

              'publisher': {
                '@id': this.seo.campaignId
              },

              'about': {
                '@id': this.seo.lydiaId
              },

              'articleSection':
                'Press Releases',

              'articleBody':
                this.cleanArticleBody(
                  release.content
                ),

              'inLanguage':
                'en-US'
            },

            {
              '@type': 'WebPage',

              '@id': articleUrl,

              'url': articleUrl,

              'name':
                release.title,

              'description':
                release.summary,

              'isPartOf': {
                '@id': this.seo.websiteId
              },

              'about': {
                '@id': this.seo.lydiaId
              },

              'mainEntity': {
                '@id': articleId
              },

              'inLanguage':
                'en-US'
            }
          ]
        });
      });
  }

  /**
   * Returns the image associated with a press release.
   *
   * If the PressRelease model has an image property,
   * that image will be used automatically.
   *
   * Otherwise the campaign's standard OG image is used.
   */
  private getReleaseImage(
    release: PressRelease
  ): string {
    const releaseWithImage =
      release as PressRelease & {
        image?: string;
      };

    if (releaseWithImage.image) {
      const image =
        releaseWithImage.image;

      if (
        image.startsWith('http://') ||
        image.startsWith('https://')
      ) {
        return image;
      }

      return `${this.baseUrl}/${image.replace(/^\/+/, '')}`;
    }

    return `${this.baseUrl}/assets/bannerFinalizedNoStars.png`;
  }

  /**
   * Converts Markdown source into cleaner plain text
   * for articleBody structured data.
   *
   * The actual page continues using ngx-markdown normally.
   */
  private cleanArticleBody(
    content: string
  ): string {
    return content

      // Markdown images
      .replace(
        /!\[([^\]]*)\]\([^)]+\)/g,
        '$1'
      )

      // Markdown links
      .replace(
        /\[([^\]]+)\]\([^)]+\)/g,
        '$1'
      )

      // Markdown headings
      .replace(
        /^#{1,6}\s*/gm,
        ''
      )

      // Block quotes
      .replace(
        /^>\s?/gm,
        ''
      )

      // Markdown emphasis / code markers
      .replace(
        /[*_~`]/g,
        ''
      )

      // Markdown bullets
      .replace(
        /^\s*[-*+]\s+/gm,
        ''
      )

      // Excessive blank lines
      .replace(
        /\n{3,}/g,
        '\n\n'
      )

      .trim();
  }

  getTwitterShareUrl(): string {
    if (!this.pressRelease?.id) {
      return '';
    }

    const pressReleaseUrl =
      `${this.baseUrl}/newsroom/${createPressReleaseSlug(
        this.pressRelease
      )}`;

    const text =
      encodeURIComponent(
        `${this.pressRelease.title} - Lydia Christensen`
      );

    return (
      `https://x.com/intent/tweet` +
      `?text=${text}` +
      `&url=${encodeURIComponent(pressReleaseUrl)}`
    );
  }

  getFacebookShareUrl(): string {
    if (!this.pressRelease?.id) {
      return '';
    }

    const pressReleaseUrl =
      `${this.baseUrl}/newsroom/${createPressReleaseSlug(
        this.pressRelease
      )}`;

    return (
      `https://www.facebook.com/sharer/sharer.php` +
      `?u=${encodeURIComponent(pressReleaseUrl)}`
    );
  }
}